import { createStore, combineReducers, applyMiddleware, compose } from "redux";
import thunk from "redux-thunk";
import { ActionType } from "typesafe-actions";
import { createEpicMiddleware, EpicMiddleware } from "redux-observable";
import { connectRouter, routerMiddleware } from "connected-react-router";

import { history } from "../types/history";
import { Effects } from "./effects";

import { appSearchReducer } from "../features/app-search/store/app-search.reducer";
import { authReducer } from "../features/auth/store/auth.reducer";
import { campaignBriefReducer } from "../pages/campaign-brief/store/campaign-brief.reducer";
import { campaignBriefsReducer } from "../pages/campaign-briefs/store/campaign-briefs.reducer";
import { commentsReducer } from "../features/comments/store/comments.reducers";
import commonReducer from "./reducers/commonReducer";
import { flyerReducer } from "../pages/flyer/store/flyer.reducer";
import executionBriefReducer from "./reducers/executionBriefsReducer";
import messagesReducer from "./reducers/messagesReducer";
import notificationsReducer from "./reducers/notificationsReducer";
import permissionsReducer from "./reducers/permissionsReducer";
import { productsReducer } from "../features/products/store/products.reducer";
import reviewStagesReducer from "./reducers/reviewStagesReducer";
import uiReducer from "../features/ui/store/ui.reducer";
import userReducer from "../features/user/store/user.reducer";
import usersReducer from "../features/users/store/users.reducer";
import { workOrdersReducer } from "./reducers/workOrdersReducer";
import { productSearchSharedReducer } from "../features/product-search-shared/store/product-search-shared.reducer";
import { damReducer } from "../features/dam/store/dam.reducer";
import { workOrderAssetsTabReducer } from "../pages/work-order/features/work-order-assets-tab/store/work-order-assets-tab.reducer";
import { fileDownloadReducer } from "../features/file-attachments/file-download/store/file-download.reducer";
import { productHistoryReducer } from "../features/product-history/store/product-history.reducer";

function createReduxLogger() {
  return require("redux-logger").createLogger({ collapsed: true });
}

function createMiddleware(
  epicMiddleware: EpicMiddleware<ActionType<any>, ActionType<any>, RootState>
) {
  const middleware = [thunk, epicMiddleware, routerMiddleware(history)];

  if (Boolean(process.env.REACT_APP_USE_REDUX_LOGGER)) {
    return [...middleware, createReduxLogger()];
  }

  return middleware;
}

const rootReducer = combineReducers({
  UI: uiReducer,
  appSearch: appSearchReducer,
  authState: authReducer,
  campaignBrief: campaignBriefReducer,
  campaignBriefs: campaignBriefsReducer,
  commentsState: commentsReducer,
  common: commonReducer,
  damState: damReducer,
  executionBriefs: executionBriefReducer,
  flyerState: flyerReducer,
  messages: messagesReducer,
  notifications: notificationsReducer,
  permissions: permissionsReducer,
  products: productsReducer,
  productHistory: productHistoryReducer,
  productSearchShared: productSearchSharedReducer,
  reviewStages: reviewStagesReducer,
  router: connectRouter(history),
  user: userReducer,
  users: usersReducer,
  workOrders: workOrdersReducer,
  workOrderAssetsTabState: workOrderAssetsTabReducer,
  fileDownload: fileDownloadReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

const composeEnhancers =
  (typeof window !== "undefined" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

const epicMiddleware = createEpicMiddleware<ActionType<any>, ActionType<any>, RootState>();

const store = createStore(
  rootReducer,
  {},
  composeEnhancers(applyMiddleware(...createMiddleware(epicMiddleware)))
);

epicMiddleware.run(Effects);

export default store;
